import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { FileText, Download, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default CMS content (matches admin editor defaults)
const defaultContent = {
  hero: {
    title: 'Resources &',
    highlight: 'Documents',
    description: 'Access important documents, policies, and resources for our members.',
  },
  empty: {
    heading: 'No resources available in this category.',
    body_markdown: 'Please check back soon for new publications and resources.',
  },
};

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredResources(resources);
    } else {
      setFilteredResources(resources.filter(item => item.category === selectedCategory));
    }
  }, [selectedCategory, resources]);

  const fetchData = async () => {
    try {
      const [resourcesRes, contentRes] = await Promise.all([
        axios.get(`${API}/resources`),
        axios.get(`${API}/page-content/resources`)
      ]);
      
      setResources(resourcesRes.data);
      setFilteredResources(resourcesRes.data);
      
      const uniqueCategories = ['All', ...new Set(resourcesRes.data.map(item => item.category))];
      setCategories(uniqueCategories);
      
      // Set page content from CMS
      const next = { ...defaultContent };
      contentRes.data.forEach(item => {
        if (item.section_name && item.content) {
          next[item.section_name] = { ...next[item.section_name], ...item.content };
        }
      });
      setContent(next);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - CMS Driven */}
      <section className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {content.hero.title} <span className="text-orange-500">{content.hero.highlight}</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {content.hero.description}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      {categories.length > 1 && (
        <section className="py-8 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 overflow-x-auto">
              <Filter size={20} className="text-gray-600 flex-shrink-0" />
              <div className="flex gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                      selectedCategory === category
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredResources.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  data-testid={`resource-card-${resource.id}`}
                  className="bg-white rounded-2xl p-6 shadow-md card-hover"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <FileText className="text-orange-500" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{resource.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="px-3 py-1 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                          {resource.category}
                        </span>
                        <a
                          href={resource.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-${resource.id}`}
                          className="inline-flex items-center text-orange-500 hover:text-orange-600 font-semibold text-sm transition-colors"
                        >
                          <Download size={16} className="mr-1" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <FileText className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-500 text-lg">{content.empty.heading}</p>
              {content.empty.body_markdown && (
                <p className="text-gray-400 text-sm mt-2">{content.empty.body_markdown}</p>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
